import type {
  BrowserOptions,
  ErrorEvent,
  Event,
  EventHint,
  RequestEventData,
} from '@sentry/nextjs';

import { isBenignClientDisconnectMessage } from './benign-client-disconnect';

type TransactionEvent = Parameters<NonNullable<BrowserOptions['beforeSendTransaction']>>[0];

const GLOBAL_SAMPLE_RATE = 0.25;

function getPrimaryException(event: ErrorEvent) {
  return event.exception?.values?.[0];
}

function getErrorMessage(event: ErrorEvent): string {
  return getPrimaryException(event)?.value ?? event.message ?? '';
}

function getErrorType(event: ErrorEvent): string {
  return getPrimaryException(event)?.type ?? '';
}

function shouldSample(rate: number): boolean {
  return Math.random() < rate;
}

function stripQueryAndFragment(url: string): string {
  const end = url.search(/[?#]/);
  return end === -1 ? url : url.slice(0, end);
}

// Some values are element names or CSS selectors that legitimately contain '#', so only
// URL-shaped values are cut.
function stripIfUrl(value: string): string {
  return /^(?:https?:\/\/|\/)/i.test(value) ? stripQueryAndFragment(value) : value;
}

// Only the request method, its URL without query or fragment, and these headers may
// leave for Sentry. An allowlist, so a field or header added later stays out until
// someone names it here.
const ALLOWED_REQUEST_HEADERS = new Map<string, (value: string) => string>([
  ['user-agent', (value) => value],
  ['x-vercel-id', (value) => value],
  ['referer', stripQueryAndFragment],
]);

function scrubRequest({ method, url, headers = {} }: RequestEventData): RequestEventData {
  const allowedHeaders: Record<string, string> = {};
  for (const [name, value] of Object.entries(headers)) {
    const sanitize = ALLOWED_REQUEST_HEADERS.get(name.toLowerCase());
    if (sanitize) {
      allowedHeaders[name] = sanitize(value);
    }
  }

  return { method, url: url && stripQueryAndFragment(url), headers: allowedHeaders };
}

const URL_DATA_SANITIZERS = new Map<string, (value: string) => string>([
  ['url', stripQueryAndFragment],
  ['http.url', stripQueryAndFragment],
  ['from', stripQueryAndFragment],
  ['to', stripQueryAndFragment],
  ['lcp.url', stripQueryAndFragment],
  ['code.filepath', stripQueryAndFragment],
  ['browser.script.invoker', stripIfUrl],
]);

function scrubUrlData(data: Record<string, unknown>): void {
  for (const [key, sanitize] of URL_DATA_SANITIZERS) {
    const value = data[key];
    if (typeof value === 'string') {
      data[key] = sanitize(value);
    }
  }
  delete data['http.query'];
  delete data['http.fragment'];
}

// Script frames keep their exact filename, which debug_meta already copied for
// source-map matching. Frames from inline document code have no debug ID, so they are
// stripped like any URL.
function scrubFrameFilename(filename: string): string {
  const path = stripQueryAndFragment(filename);
  return /\.[cm]?js$/i.test(path) ? filename : path;
}

function scrubEvent<T extends Event>(event: T): T {
  if (event.request) {
    event.request = scrubRequest(event.request);
  }
  for (const breadcrumb of event.breadcrumbs ?? []) {
    if (breadcrumb.data) {
      scrubUrlData(breadcrumb.data);
    }
  }
  for (const span of event.spans ?? []) {
    scrubUrlData(span.data);
    if (span.description) {
      span.description = stripIfUrl(span.description);
    }
  }
  const traceData = event.contexts?.trace?.data;
  if (traceData) {
    scrubUrlData(traceData);
  }
  const nextjs = event.contexts?.nextjs;
  if (typeof nextjs?.request_path === 'string') {
    nextjs.request_path = stripQueryAndFragment(nextjs.request_path);
  }
  for (const exception of event.exception?.values ?? []) {
    for (const frame of exception.stacktrace?.frames ?? []) {
      if (frame.filename) {
        frame.filename = scrubFrameFilename(frame.filename);
      }
    }
  }
  return event;
}

export function serverBeforeSend(event: ErrorEvent, _hint: EventHint): ErrorEvent | null {
  // Drop the benign client-disconnect family before sampling so it never reaches
  // Sentry (mirrors the unhandled-rejection guard's downgrade of the same family).
  if (isBenignClientDisconnectMessage(getErrorMessage(event))) {
    return null;
  }

  return shouldSample(GLOBAL_SAMPLE_RATE) ? scrubEvent(event) : null;
}

export function clientBeforeSend(event: ErrorEvent, _hint: EventHint): ErrorEvent | null {
  const message = getErrorMessage(event);
  const type = getErrorType(event);

  if (
    (type === 'TRPCClientError' || message.includes('TRPCClientError')) &&
    message.includes('opps-api.getwarmly.com')
  ) {
    return null;
  }

  return shouldSample(GLOBAL_SAMPLE_RATE) ? scrubEvent(event) : null;
}

export function clientBeforeSendTransaction(
  event: TransactionEvent,
  _hint: EventHint,
): TransactionEvent {
  return scrubEvent(event);
}
